/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import React, { useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { withFormikDevtools } from 'formik-devtools-extension';
import { StyledProvisioningPageHeading } from '../StyledProvisioningPageHeading';
import { StyledProvisioningPageSubheading } from '../StyledProvisioningPageSubheading';
import { Stack } from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import { BeamInput } from '../../common/formik/BeamInput';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAvailableServiceTiers,
  selectCustomerGroupTypeOptions,
  selectPlatformTypeOptions,
  selectTerminalTypeOptions,
} from '../../../store';
import {
  HAN_TERMINAL_TYPE,
  MAC_ADDRESS_NETWORKS,
  SatelliteNetwork,
  SAT_IP_NETWORKS,
  TerminalFormSchema,
  TPK_NETWORKS,
  SERIAL_NUMBER_NETWORKS,
} from '../../../shared/entities/provisionedTerminal';
import { BeamSelect } from '../../common/formik/BeamSelect';
import { BeamCancelButton } from '../../common/formik/BeamCancelButton';
import { BeamSubmitButton } from '../../common/formik/BeamSubmitButton';
import { BeamMultiSelect } from '../../common/formik/BeamMultiSelect';
import { BeamSwitch } from '../../common/formik/BeamSwitch';
import {
  requestCustomerGroupOptions,
  requestPlatformTypeOptions,
  requestTerminalTypeOptions,
  requestModemTypeOptions,
} from '../../../store/options/actions';
import { getAvailableServiceTiers } from '../../../store/serviceTierRequests/actions';
import ErrorMessage from '../../common/layout/ErrorMessage';
import { ajaxRaw } from '../../../utils/StoreUtils';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { DataPaths } from './DataPaths';
import * as yup from 'yup';
import ModalLoadingSpinner from '../../common/layout/ModalLoadingSpinner';
import { FTI_PATTERN, MAC_ADDR_PATTERN } from '../../../utils/RegexLib';
import {
  convertProvisionedTerminalToTerminalFormSchema,
  EnclosureGroups,
  enrichEnclosureLabels,
} from './EnclosureGroups';

const TerminalForm = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  let { uuid } = useParams();

  const customerGroups = useSelector(selectCustomerGroupTypeOptions);
  const serviceTiers = useSelector(selectAvailableServiceTiers);
  const platformTypes = useSelector(selectPlatformTypeOptions);
  const terminalTypes = useSelector(selectTerminalTypeOptions);

  const [submissionError, setError] = useState<string | undefined>(undefined);
  const [initialFormState, setInitialFormState] = useState<TerminalFormSchema>({
    belongsToOrgs: [],
    contracts: [],
    customerGroup: '',
    datapaths: [],
    displayName: '',
    id: '',
    platformType: '',
    restrictedDefault: false,
    serviceTier: '',
    tags: [],
    terminalType: '',
    hanAgentId: '',
    haloNetSpaceVehicleName: '',
    enclosureGroups: [],
  });

  // Run actions on initial load
  useEffect(() => {
    // Request options
    dispatch(requestCustomerGroupOptions());
    dispatch(getAvailableServiceTiers());
    dispatch(requestPlatformTypeOptions());
    dispatch(requestTerminalTypeOptions());
    dispatch(requestModemTypeOptions());
  }, [dispatch]);

  const isEditMode = uuid !== undefined;
  const action = isEditMode ? 'Edit' : 'Add';

  const [isLoadingTerminal, setIsLoadingTerminal] = useState(false);
  useEffect(() => {
    // Request existing data if we're editing an entity
    if (isEditMode) {
      setIsLoadingTerminal(true);
      ajaxRaw.get(`/provisioning/terminals/${uuid}`).subscribe({
        next: value => {
          setIsLoadingTerminal(false);

          // Convert ProvisionedTerminal to TerminalFormSchema
          const initialTerminalFormSchema: TerminalFormSchema = convertProvisionedTerminalToTerminalFormSchema(
            value.response,
          );

          // Set the initial form state
          setInitialFormState(initialTerminalFormSchema);
        },
        error: err => {
          setIsLoadingTerminal(false);
          setError(err.message);
        },
      });
    }
  }, [isEditMode, uuid]);

  return (
    <>
      <StyledProvisioningPageHeading>
        {action} {!isEditMode && 'new '}terminal
      </StyledProvisioningPageHeading>
      <StyledProvisioningPageSubheading>
        To {action.toLowerCase()} a {!isEditMode && 'new '}terminal, please complete the form below.
      </StyledProvisioningPageSubheading>
      {isLoadingTerminal ? <ModalLoadingSpinner /> : null}

      <Formik
        enableReinitialize={true}
        initialValues={initialFormState}
        onSubmit={(values, { setSubmitting }: FormikHelpers<TerminalFormSchema>) => {
          setSubmitting(true);
          let url = `/provisioning/terminals`;
          let operation = ajaxRaw.postJson;
          if (isEditMode) {
            url = `/provisioning/terminals/${values.id}`;
            operation = ajaxRaw.put;
          }

          // Convert TerminalFormSchema to ProvisionedTerminal
          const provisionedTerminal = enrichEnclosureLabels(values);

          operation(url, JSON.stringify(provisionedTerminal)).subscribe({
            next: (response: AjaxResponse<any>) => {
              setSubmitting(false);
              if (response.status === 200 || response.status === 201) {
                history.goBack();
              } else {
                setError(response.response);
              }
            },
            error: (err: AjaxError) => {
              setError(err.response.message ? err.response.message : err.message);
              setSubmitting(false);
            },
          });
        }}
        validationSchema={yup.object().shape({
          customerGroup: yup.string().required('Please select a customer group'),
          displayName: yup.string().min(1).max(255).required('Please enter a display name'),
          serviceTier: yup.string().optional().nullable(),
          platformType: yup.string().required('Please select a platform type'),
          terminalType: yup.string().required('Please select a terminal type'),
          hanAgentId: yup.string().max(255, 'HAN Agent ID must be 255 characters or fewer').optional().nullable(),
          haloNetSpaceVehicleName: yup.string().when('terminalType', {
            is: 'HaloNet',
            then: schema => schema.required('Please enter the HaloNet Space Vehicle Name'),
            otherwise: schema => schema.optional().nullable(),
          }),
          tags: yup.array().of(yup.string().max(255)).optional().nullable(),
          datapaths: yup
            .array()
            .of(
              yup.object().shape({
                label: yup.string().max(255).required('Please enter a data path label'),
                antenna: yup.string().required('Please select an antenna'),
                modems: yup.array().of(
                  yup.object().shape({
                    type: yup
                      .string()
                      .test('type', 'Please select a modem type', (value, ctx) => {
                        // Amount of levels deep this field is nested within the form's validationSchema (index at 0)
                        const modemNestedLevelCount = 2;

                        const terminalType =
                          ctx.from && ctx.from[modemNestedLevelCount]
                            ? (ctx.from[modemNestedLevelCount].value.terminalType as string)
                            : undefined;

                        // Require modem type field for HAN terminals only
                        if (terminalType === HAN_TERMINAL_TYPE) {
                          return value !== undefined && value !== null && value !== '';
                        } else {
                          return true; // Automatically pass validation test
                        }
                      })
                      .nullable(),
                    networks: yup
                      .array(yup.string<SatelliteNetwork>().required('A network must be selected'))
                      .min(1, 'A network must be selected')
                      .max(1, 'Only one network can be selected'),
                    satIp: yup.string().when(['networks'], ([networks], schema) => {
                      if (networks === undefined) return schema.notRequired();
                      const satelliteNetworks = networks as SatelliteNetwork[];
                      return satelliteNetworks.some(network => SAT_IP_NETWORKS.includes(network))
                        ? schema.required('Please enter a satellite IP (FTI)').matches(new RegExp(`^${FTI_PATTERN}$`), {
                            message: 'Format invalid',
                          })
                        : schema.notRequired();
                    }),
                    macAddress: yup.string().when(['networks'], ([networks], schema) => {
                      if (networks === undefined) return schema.notRequired();
                      const satelliteNetworks = networks as SatelliteNetwork[];
                      return satelliteNetworks.some(network => MAC_ADDRESS_NETWORKS.includes(network))
                        ? schema.required('Please enter a MAC address').matches(new RegExp(`^${MAC_ADDR_PATTERN}$`), {
                            message: 'Format invalid',
                          })
                        : schema.notRequired();
                    }),
                    tpk: yup.string().when(['networks', 'type'], ([networks, type], schema) => {
                      if (networks === undefined) return schema.notRequired();
                      const satelliteNetworks = networks as SatelliteNetwork[];
                      return satelliteNetworks.some(network => TPK_NETWORKS.includes(network)) && type !== 'Axiom Air'
                        ? schema.required('Please enter a Terminal Provisioning Key (TPK)')
                        : schema.notRequired();
                    }),
                    cdpAssetId: yup.string().when(['networks', 'type'], ([networks, type], schema) => {
                      if (networks === undefined) return schema.notRequired();
                      const satelliteNetworks = networks as SatelliteNetwork[];
                      return satelliteNetworks.some(network => SERIAL_NUMBER_NETWORKS.includes(network)) &&
                        type === 'Axiom Air'
                        ? schema.required('Please enter a CDP Asset ID')
                        : schema.notRequired();
                    }),
                    serialNumber: yup.string().when(['networks', 'type'], ([networks, type], schema) => {
                      if (networks === undefined) return schema.notRequired();
                      const satelliteNetworks = networks as SatelliteNetwork[];
                      return satelliteNetworks.some(network => SERIAL_NUMBER_NETWORKS.includes(network)) &&
                        type === 'Axiom Air'
                        ? schema.required('Please enter a serial number')
                        : schema.notRequired();
                    }),
                  }),
                ),
              }),
            )
            .min(1)
            .required('Please configure at least one data path'),
          enclosureGroups: yup
            .array()
            .of(
              yup.object().shape({
                label: yup.string().max(255),
                modemFormIdentifiers: yup
                  .array()
                  .of(yup.string().max(255))
                  .min(1, 'At least one modem must be selected'),
              }),
            )
            .optional()
            .nullable(),
        })}
      >
        {formikProps => {
          withFormikDevtools(formikProps);
          return (
            <Form
              autoComplete={'off'}
              style={{ width: '600px', paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '18px' }}
            >
              {submissionError ? <ErrorMessage>{submissionError}</ErrorMessage> : null}

              <BeamInput name={'displayName'} label={'Display Name'} required={true} />

              <BeamSelect
                name={'customerGroup'}
                label={'Customer Group'}
                options={customerGroups ?? []}
                placeholder={'Select customer group'}
                required={true}
                getOptionLabel={option => option.toUpperCase()}
                disabled={isEditMode}
              />

              <BeamSelect
                name={'serviceTier'}
                label={'Service Tier'}
                options={serviceTiers ?? []}
                placeholder={'Select service tier'}
                optional={true}
              />

              <BeamSelect
                name={'platformType'}
                label={'Platform Type'}
                options={platformTypes ?? []}
                placeholder={'Select platform type'}
                required={true}
              />

              <BeamSelect
                name={'terminalType'}
                label={'Terminal Type'}
                options={terminalTypes ?? []}
                placeholder={'Select terminal type'}
                required={true}
              />

              {formikProps.values.terminalType === HAN_TERMINAL_TYPE && (
                <BeamInput
                  name={'hanAgentId'}
                  label={'HAN Agent ID'}
                  optional={true}
                  placeholder={isEditMode ? undefined : 'Defaults to Terminal UUID if omitted'}
                  helpText={
                    isEditMode
                      ? 'Optional custom ID for offline provisioning.'
                      : 'Optional custom ID for offline provisioning. Leave blank to default to Terminal UUID.'
                  }
                />
              )}

              {formikProps.values.terminalType === 'HaloNet' && (
                <BeamInput name={'haloNetSpaceVehicleName'} label={'HaloNet Space Vehicle Name'} required={true} />
              )}

              <DataPaths />

              <EnclosureGroups />

              {/* Future improvement: set `options` to the list of tags already in use on other terminals */}
              <BeamMultiSelect name={'tags'} label={'Tags'} options={[]} freeSolo={true} optional={true} />

              <BeamSwitch
                name={'restrictedDefault'}
                label={'Default to restricted?'}
                trueLabel={'Yes'}
                falseLabel={'No'}
              />

              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <BeamCancelButton />
                <BeamSubmitButton />
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default TerminalForm;
